import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import SpinnerArea from "../../../../components/SpinnerArea";
import MainContext from "../../../../context/mainContext";
import useService from "../../../../hooks/useService";
import { FinalVersionModel } from "../../../../models/workflow.model";
import workflowService from "../../../../services/workflow.service";
import WorkflowContext from "../../context/WorkflowContext";
import * as Yup from "yup";
import styles from "./FinalVersion.module.scss";

import SaveSuccessDialog from "../../SaveSuccessDialog";
import CancelDialog from "../../CancelDialog";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import IconTooltip from "../../../../components/IconTooltip";
import PlaceholderControl from "../../../../components/PlaceholderControl";
import FinaVersionDocuments from "./FinalVersionDocuments";
import globalDataService from "../../../../services/globalData.service";
import { validateBeforeExecute } from "../../../../misc/utils.functions";

const _emptyFinalVersion: FinalVersionModel = {
  isCompleted: false,
  canEdit: true,
  certified: false,
  documents: [],
  selectedLanguage: undefined,
};

const FinalVersion: React.FC = () => {
  const { ticketNumber } = useParams();
  const { service } = useContext(MainContext);
  const { currentWorkflow, reload } = useContext(WorkflowContext);
  const { t } = useTranslation();
  const [model, loading, setData] = useService(() =>
    workflowService.finalVersion(ticketNumber!)
  );

  const [availableLanguages, loadingLanguages] = useService(() =>
    globalDataService.languages()
  );

  const [isReadOnly, setIsReadOnly] = useState(false);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [cancelDialogShow, cancelDialogShowSet] = useState(false);
  const [uploadDocuments, setUploadDocuments] = useState<FileWrapper[]>([]);

  const [requiredDocumentError, setRequiredDocumentError] = useState<
    boolean | null
  >(null);

  const validationSchema = Yup.object().shape({
    certified: Yup.boolean().nullable().oneOf([true], t("certifiedRequired")!),
  });

  const formik = useFormik<FinalVersionModel>({
    initialValues: model || _emptyFinalVersion,
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const save = (values: FinalVersionModel, nextStep: boolean) => {
    service(
      () =>
        workflowService.finalVersionSave(
          ticketNumber!,
          values,
          nextStep,
          uploadDocuments
        ),
      {
        callback: (response) => {
          if (!nextStep) setSaveSuccess(true);
          else {
            reload();
            navigate(
              `/workflow/${response.ticketNumber}/${response.nextStep}`,
              { relative: "path" }
            );
          }
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<FinalVersionModel>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  const onDelete = (index: number) => {
    if (model?.documents) {
      const aux = [...model?.documents];
      aux.splice(index, 1);

      setData({ ...model, documents: [...aux] });
    }
  };

  const handleSaveCondition = (): boolean => {
    console.log(formik.errors);
    if (
      uploadDocuments?.length > 0 ||
      (model?.documents?.length != undefined && model?.documents?.length > 0)
    ) {
      setRequiredDocumentError(null);
      return true;
    } else {
      setRequiredDocumentError(true);
      return false;
    }
  };

  const handleContinueCondition = (): boolean => {
    var saveCondition = handleSaveCondition();

    return (
      saveCondition &&
      (uploadDocuments.length !== 0 || model?.documents.length !== 0) &&
      formik.values.certified
    );
  };

  useEffect(() => {
    if (model) {
      setIsReadOnly(model.isCompleted || !model.canEdit);
    }
  }, [model]);

  return (
    <SpinnerArea loading={loading || saving || loadingLanguages}>
      <form
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        className={styles.finalVersionForm}
      >
        <Grid container>
          <Grid item sm={12}>
            <div className={styles.header}>
              <p>
                <strong>{t("publicationTicketNumber")}: </strong>
                {currentWorkflow?.ticketNumber}
              </p>
              <p>
                <strong>{t("uploadFinalVersion")}</strong>
              </p>
              <p className={styles.message}>{t("finalVersionMessage1")}</p>
              <p className={styles.message}>{t("finalVersionMessage2")}</p>
            </div>
          </Grid>
        </Grid>
        <div className={styles.formContainer}>
          <div className={styles["item-1"]}>
            <FormControl fullWidth>
              <FormLabel id="docType-label">{t("mainLanguage")}</FormLabel>
              <Select
                id="selectedLanguage"
                name="selectedLanguage"
                className={
                  formik.touched.selectedLanguage &&
                  formik.errors.selectedLanguage
                    ? "Mui-error"
                    : ""
                }
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
                value={formik.values.selectedLanguage || ""}
                onChange={(event: SelectChangeEvent) => {
                  formik.setFieldValue("selectedLanguage", event.target.value);
                }}
                onBlur={formik.handleBlur}
                displayEmpty
                renderValue={(value) =>
                  value !== "" ? (
                    t(`languages.${value}`)
                  ) : (
                    <PlaceholderControl text={t("selectLanguages")} />
                  )
                }
              >
                <MenuItem
                  key={`languages_${0}`}
                  value={0}
                  className={"selectPlaceholder"}
                >
                  {t("languages")}
                </MenuItem>
                {availableLanguages?.map((x) => (
                  <MenuItem key={`languages_${x}`} value={x}>
                    {t(`languages.${x}`)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText className="Mui-error">
                {/* {getIn(formik.touched, "docType.id") && getIn(formik.errors, "docType.id")} */}
              </FormHelperText>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  id="certified"
                  name="certified"
                  checked={formik.values.certified}
                  disabled={model?.isCompleted || isReadOnly}
                  onChange={(event: ChangeEvent<any>, checked: boolean) => {
                    formik.setFieldValue("certified", checked);
                  }}
                  onBlur={formik.handleBlur}
                />
              }
              label={t("finalVersionCertified")}
            />
            {formik.touched.certified && (
              <FormHelperText error>{formik.errors.certified}</FormHelperText>
            )}

            <FileUploader
              onChange={(file: File | File[] | FileWrapper[]) => {
                setUploadDocuments(file as FileWrapper[]);
                formik.setFieldValue("selectedLanguage", null);
              }}
              allowedExtensions={[".pdf"]}
              isReadOnly={isReadOnly || !formik.values.selectedLanguage}
              title={t("uploadFinalVersion")!}
              appendMode
              prefix={formik.values.selectedLanguage}
              errorValidate={requiredDocumentError}
            />
          </div>
          <div className={styles["item-2"]}>
            {model?.documents && model.documents.length > 0 && (
              <FinaVersionDocuments
                documents={model.documents}
                onDelete={isReadOnly ? undefined : onDelete}
              />
            )}
          </div>
        </div>
        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>
          <Button
            className={styles.save}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => save(formik.values, false),
                handleSaveCondition
              )
            }
          >
            {t("save")}
          </Button>
          <Button
            className={styles.continue}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => save(formik.values, true),
                handleContinueCondition
              )
            }
          >
            {t("continue")}
          </Button>
        </div>
      </form>
      <SaveSuccessDialog open={saveSuccess} />
      <CancelDialog
        open={cancelDialogShow}
        yesAction={() => navigate(-1)}
        noAction={() => cancelDialogShowSet(false)}
      />
    </SpinnerArea>
  );
};

export default FinalVersion;
